import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  doc,
  updateDoc,
  limit
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Paperclip, 
  Smile, 
  MoreVertical, 
  Search,
  User as UserIcon,
  Check,
  CheckCheck,
  MessageSquare,
  X,
  FileIcon,
  Image as ImageIcon
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const Messages: React.FC = () => {
  const { user, profile } = useAuth();
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConv, setSelectedConv] = useState<any>(null);
  const [showListOnMobile, setShowListOnMobile] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!profile) return;

    // Fetch conversations where user is a participant and matches tenant
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', profile.uid),
      where('tenantId', '==', profile.tenantId || ''),
      orderBy('updatedAt', 'desc')
    );

    const unsubConvs = onSnapshot(q, (snapshot) => {
      setConversations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubConvs();
  }, [profile]);

  useEffect(() => {
    if (!selectedConv) return;

    // Fetch messages for selected conversation
    const q = query(
      collection(db, `conversations/${selectedConv.id}/messages`),
      orderBy('createdAt', 'asc'),
      limit(100)
    );

    const unsubMsgs = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    return () => unsubMsgs();
  }, [selectedConv]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConv || !user) return;

    const text = newMessage;
    const currentAttachments = [...attachments];
    setNewMessage('');
    setAttachments([]);

    try {
      const msgData = {
        senderId: user.uid,
        text,
        attachments: currentAttachments,
        createdAt: new Date().toISOString(),
        readBy: [user.uid]
      };

      await addDoc(collection(db, `conversations/${selectedConv.id}/messages`), msgData);
      
      // Update conversation last message and timestamp
      await updateDoc(doc(db, 'conversations', selectedConv.id), {
        lastMessage: { text: text || 'Sent an attachment', senderId: user.uid, createdAt: new Date().toISOString() },
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'messages');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = Array.from(files).map((file: File) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      isImage: file.type.startsWith('image/')
    }));

    setAttachments([...attachments, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  return (
    <div className="h-[calc(100vh-160px)] flex flex-col md:flex-row bg-[#0A0A0A] border border-white/5 rounded-3xl overflow-hidden shadow-2xl relative">
      {/* Sidebar - Conversation List */}
      <div className={cn(
        "w-full md:w-80 border-r border-white/5 flex flex-col transition-all duration-300",
        !showListOnMobile && selectedConv ? "hidden md:flex" : "flex"
      )}>
        <div className="p-6 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input 
              type="text" 
              placeholder="Search chats..."
              className="w-full bg-black border border-white/10 rounded-xl pl-10 pr-4 py-2 text-xs focus:outline-none focus:border-blue-500/50 transition-all"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-white/[0.02] custom-scrollbar">
          {conversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => {
                setSelectedConv(conv);
                setShowListOnMobile(false);
              }}
              className={cn(
                "w-full p-4 flex items-start gap-3 text-left transition-all hover:bg-white/[0.02]",
                selectedConv?.id === conv.id ? "bg-white/[0.05]" : ""
              )}
            >
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center shrink-0 border border-blue-500/20">
                <UserIcon size={20} className="text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold truncate">
                    {conv.type === 'ticket' ? `Ticket Support` : 'Direct Chat'}
                  </p>
                  <span className="text-[10px] text-gray-500">
                    {formatDistanceToNow(new Date(conv.updatedAt), { addSuffix: false })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {conv.lastMessage?.senderId === user?.uid ? 'You: ' : ''}
                  {conv.lastMessage?.text}
                </p>
              </div>
            </button>
          ))}
          {conversations.length === 0 && (
            <div className="p-10 text-center">
              <p className="text-xs text-gray-500">No conversations yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={cn(
        "flex-1 flex flex-col min-w-0 bg-black/20 transition-all duration-300",
        showListOnMobile && !selectedConv ? "hidden md:flex" : "flex",
        !selectedConv && "hidden md:flex"
      )}>
        {selectedConv ? (
          <>
            {/* Chat Header */}
            <div className="h-16 border-b border-white/5 px-4 md:px-6 flex items-center justify-between bg-[#0A0A0A]/50">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowListOnMobile(true)}
                  className="p-2 -ml-2 text-gray-400 hover:text-white md:hidden"
                >
                  <X size={20} />
                </button>
                <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center border border-blue-500/20">
                  <UserIcon size={16} className="text-blue-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold">
                    {selectedConv.type === 'ticket' ? `Support Team` : 'Direct Chat'}
                  </h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">Online</span>
                  </div>
                </div>
              </div>
              <button className="p-2 text-gray-500 hover:text-white">
                <MoreVertical size={20} />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              {messages.map((msg, i) => {
                const isMe = msg.senderId === user?.uid;
                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "flex flex-col max-w-[80%]",
                      isMe ? "ml-auto items-end" : "mr-auto items-start"
                    )}
                  >
                    <div className={cn(
                      "px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-lg",
                      isMe 
                        ? "bg-blue-500 text-white rounded-tr-none" 
                        : "bg-[#1A1A1A] text-gray-200 rounded-tl-none border border-white/5"
                    )}>
                      {msg.text}
                      
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {msg.attachments.map((att: any, idx: number) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-black/20 rounded-lg border border-white/5">
                              {att.isImage ? <ImageIcon size={14} /> : <FileIcon size={14} />}
                              <span className="text-[10px] truncate max-w-[150px]">{att.name}</span>
                              <a href={att.url} target="_blank" rel="noreferrer" className="ml-auto text-[10px] text-blue-400 hover:underline">View</a>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 px-1">
                      <span className="text-[10px] text-gray-500">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMe && (
                        <CheckCheck size={12} className="text-blue-500" />
                      )}
                    </div>
                  </motion.div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-6 bg-[#0A0A0A]/50 border-t border-white/5">
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[10px]">
                      {att.isImage ? <ImageIcon size={12} /> : <FileIcon size={12} />}
                      <span className="truncate max-w-[100px]">{att.name}</span>
                      <button onClick={() => removeAttachment(idx)} className="text-gray-500 hover:text-white">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <form onSubmit={handleSendMessage} className="flex items-end gap-3">
                <div className="flex-1 bg-black border border-white/10 rounded-2xl p-2 flex items-end">
                  <input 
                    type="file" 
                    multiple 
                    className="hidden" 
                    ref={fileInputRef} 
                    onChange={handleFileSelect}
                  />
                  <button 
                    type="button" 
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-500 hover:text-white transition-colors"
                  >
                    <Paperclip size={20} />
                  </button>
                  <textarea
                    rows={1}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message..."
                    className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-2 px-2 resize-none max-h-32 custom-scrollbar"
                  />
                  <button type="button" className="p-2 text-gray-500 hover:text-white transition-colors">
                    <Smile size={20} />
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={!newMessage.trim() && attachments.length === 0}
                  className="w-24 h-24 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl flex items-center justify-center transition-all active:scale-90 disabled:opacity-50 disabled:bg-gray-800 shadow-lg shadow-blue-500/20"
                >
                  <Send size={20} />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mb-6 border border-white/10">
              <MessageSquare size={32} className="text-gray-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Select a conversation</h3>
            <p className="text-gray-500 max-w-xs text-sm">Choose a chat from the sidebar to start messaging with our support team.</p>
          </div>
        )}
      </div>
    </div>
  );
};
